import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import type { Currency } from "@/lib/finance";
import { money, moneyIn, rateText } from "@/lib/format";
import { MoneyInput, parseMoney } from "@/components/MoneyInput";
import { Pick } from "@/components/movements/TransactionFormDialog";

export interface FxState { fx: string; dest: string; orig: string; origCur: string }
interface Props {
  s: FxState; set: (k: keyof FxState) => (v: string) => void; amount: string; date: string;
  srcCur: Currency | null; dstCur: Currency | null; cross: boolean; foreign: Currency | null; origAllowed: boolean;
}

function useDayRate(cur: Currency | null, date: string) {
  const [rate, setRate] = useState<number | null>(null);
  useEffect(() => {
    if (!cur || cur === "MXN" || !date) { setRate(null); return; }
    api.get<{ rates: Record<string, number | null> }>("/fx/on", { params: { date } }).then((r) => setRate(r.data.rates[cur] ?? null)).catch(() => setRate(null));
  }, [cur, date]);
  return rate;
}

function CrossInfo({ amount, dest, srcCur, dstCur }: { amount: number; dest: number; srcCur: Currency; dstCur: Currency }) {
  if (!(amount > 0 && dest > 0)) return null;
  const text = srcCur === "MXN" ? `${rateText(amount / dest)} por ${moneyIn(1, dstCur)}`
    : dstCur === "MXN" ? `${rateText(dest / amount)} por ${moneyIn(1, srcCur)}`
    : `${moneyIn(1, srcCur)} = ${moneyIn(dest / amount, dstCur)}`;
  return <p className="text-sm text-slate-600" data-testid="tx-cross-rate">Tipo de cambio que resulta: <b className="text-[#1B2A4A]">{text}</b></p>;
}

export function FxFields({ s, set, amount, date, srcCur, dstCur, cross, foreign, origAllowed }: Props) {
  const dayRate = useDayRate(foreign ?? (cross && srcCur !== "MXN" && dstCur !== "MXN" ? srcCur : null), date);
  const amt = parseMoney(amount);
  const custom = parseMoney(s.fx);
  const rate = custom > 0 ? custom : dayRate;
  return (
    <>
      {cross && srcCur && dstCur && (
        <div className="space-y-1.5">
          <Label htmlFor="tx-dest-amount" className="text-sm text-slate-700">Monto que entra ({dstCur})</Label>
          <MoneyInput id="tx-dest-amount" value={s.dest} onChange={set("dest")} className="h-11" testId="tx-dest-amount-input" />
          <CrossInfo amount={amt} dest={parseMoney(s.dest)} srcCur={srcCur} dstCur={dstCur} />
        </div>
      )}
      {foreign && (
        <div className="space-y-1.5">
          <Label htmlFor="tx-fx-rate" className="text-sm text-slate-700">Tipo de cambio (pesos por {moneyIn(1, foreign)})</Label>
          <Input id="tx-fx-rate" inputMode="decimal" value={s.fx} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("fx")(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder={dayRate ? String(dayRate) : "Sin dato de Banxico"} className="h-11" data-testid="tx-fx-rate-input" />
          <p className="text-xs text-slate-500" data-testid="tx-fx-info">
            {dayRate ? `FIX de Banxico de ese día: ${rateText(dayRate)}. Si tu banco te dio otro, escríbelo.` : "No hay tipo de cambio de Banxico para esa fecha; escríbelo."}
            {rate && amt ? <> Equivale a <b className="text-[#1B2A4A]">{money(Math.abs(amt) * rate)} MXN</b>.</> : null}
          </p>
        </div>
      )}
      {origAllowed && (
        <div className="grid grid-cols-[1fr_140px] gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="tx-orig-amount" className="text-sm text-slate-700">Monto original (opcional)</Label>
            <MoneyInput id="tx-orig-amount" value={s.orig} onChange={set("orig")} className="h-11" testId="tx-orig-amount-input" />
          </div>
          <Pick label="Moneda" value={s.origCur} onChange={set("origCur")} testId="tx-orig-currency" placeholder="Moneda"
            options={[{ value: "USD", label: "USD" }, { value: "EUR", label: "EUR" }]} />
        </div>
      )}
    </>
  );
}
